/*
 * Product tile highlight-on-arrival
 *
 * When the URL points at a specific product, scroll that product's tile into
 * view on the collection / search results grid and flash a highlight ring for
 * a couple of seconds.
 *
 * Supported URL forms:
 *   ?highlight=<product-id>        e.g. ?highlight=1234567890
 *   ?highlight=<product-handle>    e.g. ?highlight=nova-sandal-tan
 *   #product-<product-id>          e.g. #product-1234567890
 *   #product-<product-handle>      e.g. #product-nova-sandal-tan
 *
 * Tiles are matched against the additive data attributes on <grid-item>:
 *   data-product-id, data-product-handle (see snippets/product-grid-item.liquid).
 *
 * Works on initial load and re-runs after the theme's client-side re-renders
 * (Search & Discovery filter / sort, ajaxinate pagination) via a MutationObserver
 * on the [data-products-grid] container. No match = quiet no-op.
 */
(function () {
  'use strict';

  var GRID_SELECTOR = '[data-products-grid]';
  var TILE_SELECTOR = '[data-grid-item]';
  var HIGHLIGHT_CLASS = 'is-highlighted';
  var HIGHLIGHT_MS = 2500; // visible flash duration
  var RENDER_SETTLE_MS = 150; // debounce for grid re-render mutations

  // Resolve the requested product token from the URL, or null if none.
  function getToken() {
    try {
      var params = new URLSearchParams(window.location.search);
      var q = params.get('highlight');
      if (q) return q.trim();
    } catch (e) {
      /* URLSearchParams unsupported — fall through to hash */
    }

    var hash = window.location.hash || '';
    var m = hash.match(/^#product-(.+)$/);
    if (m && m[1]) return decodeURIComponent(m[1]).trim();

    return null;
  }

  // CSS.escape fallback so attribute selectors are safe for handle tokens.
  function escapeAttr(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value).replace(/["\\\]]/g, '\\$&');
  }

  // Find the tile for the token within a grid, matching id then handle.
  function findTile(grid, token) {
    if (!grid || !token) return null;
    var safe = escapeAttr(token);
    return (
      grid.querySelector(TILE_SELECTOR + '[data-product-id="' + safe + '"]') ||
      grid.querySelector(TILE_SELECTOR + '[data-product-handle="' + safe + '"]')
    );
  }

  var timers = new WeakMap(); // per-tile cleanup timer

  function flash(tile) {
    // Restart the animation cleanly if the tile is re-highlighted.
    var pending = timers.get(tile);
    if (pending) {
      clearTimeout(pending);
      tile.classList.remove(HIGHLIGHT_CLASS);
      // Force reflow so re-adding the class replays the CSS animation.
      void tile.offsetWidth;
    }

    tile.classList.add(HIGHLIGHT_CLASS);

    var timer = setTimeout(function () {
      tile.classList.remove(HIGHLIGHT_CLASS);
      timers.delete(tile);
    }, HIGHLIGHT_MS);
    timers.set(tile, timer);
  }

  function scrollTo(tile) {
    var reduce =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try {
      tile.scrollIntoView({
        behavior: reduce ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    } catch (e) {
      tile.scrollIntoView();
    }
  }

  // Attempt to highlight the token's tile inside a grid.
  // `scroll` — whether to bring the tile into view (initial load & fresh renders).
  // Returns the matched node so callers can dedupe repeat work.
  function apply(grid, token, scroll) {
    var tile = findTile(grid, token);
    if (!tile) return null;
    if (scroll) scrollTo(tile);
    flash(tile);
    return tile;
  }

  function initGrid(grid, token) {
    // Initial pass: scroll + flash once the layout is ready.
    var lastTile = apply(grid, token, true);

    // Re-render hook: filter / sort replaces innerHTML, ajaxinate appends nodes.
    // Both are childList mutations; our class toggle is an attribute change and
    // won't retrigger the observer, so no feedback loop.
    var debounce;
    var observer = new MutationObserver(function () {
      clearTimeout(debounce);
      debounce = setTimeout(function () {
        var tile = findTile(grid, token);
        if (!tile) return; // filtered out / hidden — behave normally
        if (tile === lastTile) return; // same node persisted (e.g. append) — leave it
        lastTile = apply(grid, token, true);
      }, RENDER_SETTLE_MS);
    });
    observer.observe(grid, { childList: true, subtree: true });
  }

  function init() {
    var token = getToken();
    if (!token) return; // nothing requested — no-op

    var grids = document.querySelectorAll(GRID_SELECTOR);
    if (!grids.length) return;
    Array.prototype.forEach.call(grids, function (grid) {
      initGrid(grid, token);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
